import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createHashRouter, Navigate, RouterProvider } from "react-router";

import { AppShell } from "./shell/AppShell";
import { ApprovalsScreen } from "./features/approvals/ApprovalsScreen";
import { AttributionScreen } from "./features/attribution/AttributionScreen";
import { FillsScreen } from "./features/fills/FillsScreen";
import { OrdersScreen } from "./features/orders/OrdersScreen";
import { OverviewScreen } from "./features/overview/OverviewScreen";
import { PlaceholderScreen } from "./shell/Placeholder";
import { PositionsScreen } from "./features/positions/PositionsScreen";
import { DataSnapshotScreen } from "./features/snapshots/DataSnapshotScreen";
import { HaltScreen } from "./features/halt/HaltScreen";
import { ReconciliationScreen } from "./features/reconciliation/ReconciliationScreen";
import { InboxScreen } from "./features/inbox/InboxScreen";
import { RejectedScreen } from "./features/rejected/RejectedScreen";
import { ChangelogScreen } from "./features/changelog/ChangelogScreen";
import { SnapshotsScreen } from "./features/snapshots/SnapshotsScreen";
import { ReportsScreen } from "./features/reports/ReportsScreen";
import { PaperReplayScreen } from "./features/replay/PaperReplayScreen";
import { ValidationScreen } from "./features/validation/ValidationScreen";
import { AnalyticsScreen } from "./features/analytics/AnalyticsScreen";
import { ChangeReviewScreen } from "./features/changereview/ChangeReviewScreen";
import { RiskScreen } from "./features/risk/RiskScreen";
import { ChannelsScreen } from "./features/channels/ChannelsScreen";
import { DegradationScreen } from "./features/degradation/DegradationScreen";
import { CandidateScreen, CREDENTIALS_COPY, LOGIN_COPY } from "./features/candidate/CandidateScreen";
import { RiskReportScreen } from "./features/riskreport/RiskReportScreen";
import { ParityScreen } from "./features/parity/ParityScreen";
import { LogsScreen } from "./features/logs/LogsScreen";
import { SettingsScreen } from "./features/settings/SettingsScreen";
import { StrategyScreen } from "./features/strategy/StrategyScreen";

import "./styles/tokens.css";
import "./styles/console.css";

// 只读屏用哈希路由，屏名沿用原型（#overview / #orders?id=…）。
// 导航各屏都已有真路由；未知路径落到 PlaceholderScreen（notfound）。

const router = createHashRouter([
  {
    path: "/",
    element: <AppShell />,
    children: [
      { index: true, element: <Navigate to="/overview" replace /> },
      { path: "overview", element: <OverviewScreen /> },
      { path: "orders", element: <OrdersScreen /> },
      { path: "fills", element: <FillsScreen /> },
      { path: "positions", element: <PositionsScreen /> },
      { path: "raw-data", element: <DataSnapshotScreen /> },
      { path: "attribution", element: <AttributionScreen /> },
      { path: "approvals", element: <ApprovalsScreen /> },
      { path: "halt", element: <HaltScreen /> },
      { path: "reconciliation", element: <ReconciliationScreen /> },
      { path: "snapshots", element: <SnapshotsScreen /> },
      { path: "inbox", element: <InboxScreen /> },
      { path: "rejected", element: <RejectedScreen /> },
      { path: "changelog", element: <ChangelogScreen /> },
      { path: "reports", element: <ReportsScreen /> },
      { path: "validation", element: <ValidationScreen /> },
      { path: "analytics", element: <AnalyticsScreen /> },
      { path: "changereview", element: <ChangeReviewScreen /> },
      { path: "risk", element: <RiskScreen /> },
      { path: "riskreport", element: <RiskReportScreen /> },
      { path: "parity", element: <ParityScreen /> },
      { path: "replay", element: <PaperReplayScreen /> },
      { path: "channels", element: <ChannelsScreen /> },
      { path: "degradation", element: <DegradationScreen /> },
      { path: "credentials", element: <CandidateScreen copy={CREDENTIALS_COPY} /> },
      { path: "login", element: <CandidateScreen copy={LOGIN_COPY} showBackend /> },
      { path: "logs", element: <LogsScreen /> },
      { path: "settings", element: <SettingsScreen /> },
      { path: "strategy", element: <StrategyScreen /> },
      { path: "*", element: <PlaceholderScreen id="notfound" title="没有这一屏" /> },
    ],
  },
]);

const client = new QueryClient({
  defaultOptions: {
    queries: {
      // 服务端状态是唯一真源：切回窗口就重取，不靠本地缓存装新鲜。
      refetchOnWindowFocus: true,
      staleTime: 5_000,
    },
  },
});

const host = document.getElementById("root");
if (!host) throw new Error("index.html 缺少 #root 挂载点");

createRoot(host).render(
  <StrictMode>
    <QueryClientProvider client={client}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
